import React from 'react';
import { SaveAltOutlined } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { handleDownloadImage } from 'apps/sigma2-ui/src/components/ui/handle-download-image';

/**
 * Download button for the chart. This is used to download the chart as a PNG with the given file name.
 * The element to be downloaded but be assigned the passed in ref. That way the
 * element can be found and downloaded. The button is disabled when the chart is loading or when
 * specified by the caller.
 * @param getElement - The element getter to download. We use a getter to avoid race conditions where a ref is not yet set.
 * @param filename - The name of the chart
 * @param disabled - Whether the button is disabled
 * @param onDownloadCb - Callback that will be called after the download
 * @returns void
 *
 * @example
 * <div ref={imageRef} id="bin-scatter-multi-select">
 *   ...
 * </div>
 * <DownloadButton getElement={() => imageRef.current} filename="bin-scatter-multi-select" />
 */
export const DownloadButton: React.FC<{
  getElement: () => HTMLElement | null;
  filename: string;
  disabled?: boolean;
  onDownloadCb?: () => void;
  children?: React.ReactNode;
}> = ({ getElement, filename, disabled, onDownloadCb, children }) => {
  const [isDownloading, setIsDownloading] = React.useState(false);

  const defaultChildren = (
    <span className="font-sans flex flex-row items-center">
      {isDownloading ? (
        <CircularProgress
          color="inherit"
          size={24}
          className="text-secondary"
        />
      ) : disabled ? (
        <SaveAltOutlined className="w-6 h-6 text-gray-400" />
      ) : (
        <SaveAltOutlined className="w-6 h-6 text-secondary" />
      )}
    </span>
  );

  return (
    <button
      type="button"
      onClick={async () => {
        const $el = getElement();

        if (!$el || disabled || isDownloading) {
          return;
        }

        setIsDownloading(true);

        await handleDownloadImage($el, filename);

        if (onDownloadCb) onDownloadCb();

        setIsDownloading(false);
      }}
      className="html-to-image-filter"
    >
      {children ?? defaultChildren}
    </button>
  );
};
